import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import PartnerShell from '../components/PartnerShell';
import { getPartnerWallet, requestPartnerCashout, startPartnerOnboarding } from '../partnerApi';
import { COLOR, glass, glassRed } from '../theme';

const fmt$ = (n) => '$' + (n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const fmtDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export default function PartnerWallet() {
  const [wallet, setWallet] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await getPartnerWallet();
      setWallet(data);
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load your earnings');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Stripe sends the owner back here after Connect onboarding.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('onboarding') === 'complete') {
      setNotice('Thanks — Stripe is reviewing your details. Payouts unlock once they finish.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleOnboard = async () => {
    setBusy(true);
    setError('');
    try {
      const { url } = await startPartnerOnboarding();
      window.location.href = url;
    } catch (e) {
      setError(e.message || 'Could not start Stripe setup');
      setBusy(false);
    }
  };

  const handleCashout = async () => {
    const amount = wallet.balance.available;
    if (!window.confirm(`Cash out ${fmt$(amount)} to your bank account?`)) return;
    setBusy(true);
    setError('');
    try {
      await requestPartnerCashout();
      setNotice(`${fmt$(amount)} is on its way to your bank account.`);
      await load();
    } catch (e) {
      setError(e.message || 'Cash out failed');
    } finally {
      setBusy(false);
    }
  };

  const balance = wallet ? wallet.balance : null;
  const transactions = wallet ? wallet.transactions : [];
  // The server rejects a cashout when Stripe isn't ready; surfacing it here just
  // avoids sending the owner into a guaranteed error.
  const needsOnboarding = Boolean(error && /onboarding/i.test(error));

  return (
    <PartnerShell>
      <h1 style={S.h1}>Earnings</h1>
      <p style={S.sub}>Your share after GapGo’s commission.</p>

      {notice && <div style={S.notice}>{notice}</div>}
      {error && <div style={S.error}>{error}</div>}

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        style={S.hero}
      >
        <div style={S.heroLabel}>AVAILABLE TO CASH OUT</div>
        <div style={S.heroValue}>{balance ? fmt$(balance.available) : '—'}</div>

        <div style={S.heroSplit}>
          <div>
            <div style={S.splitValue}>{balance ? fmt$(balance.totalEarned) : '—'}</div>
            <div style={S.splitLabel}>Earned all time</div>
          </div>
          <div style={S.splitDivider} />
          <div>
            <div style={S.splitValue}>{balance ? fmt$(balance.totalPaidOut) : '—'}</div>
            <div style={S.splitLabel}>Already paid out</div>
          </div>
        </div>

        {needsOnboarding ? (
          <button style={{ ...S.cta, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={handleOnboard}>
            {busy ? 'Opening Stripe…' : 'Set up bank details'}
          </button>
        ) : (
          <button
            style={{ ...S.cta, opacity: busy || !balance || balance.available <= 0 ? 0.5 : 1 }}
            disabled={busy || !balance || balance.available <= 0}
            onClick={handleCashout}
          >
            {busy ? 'Processing…' : balance && balance.available > 0 ? `Cash out ${fmt$(balance.available)}` : 'Nothing to cash out yet'}
          </button>
        )}
        <p style={S.ctaNote}>Paid to your bank by Stripe, usually within 1–2 business days.</p>
      </motion.div>

      <h2 style={S.h2}>History</h2>
      {!wallet ? (
        <div style={S.empty}>Loading…</div>
      ) : transactions.length === 0 ? (
        <div style={S.empty}>No transactions yet.</div>
      ) : (
        <div style={S.list}>
          {transactions.map((t, i) => {
            const isPayout = t.type === 'payout';
            return (
              <div key={t.ledgerId || i} style={S.row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.rowTitle}>{isPayout ? 'Cashed out' : 'Order payment'}</div>
                  <div style={S.rowMeta}>
                    {fmtDate(t.createdAt)}
                    {!isPayout && t.commissionRate != null && ` · ${t.commissionRate}% commission`}
                  </div>
                </div>
                <div style={{ ...S.rowAmount, color: isPayout ? COLOR.textMuted : '#4ADE80' }}>
                  {isPayout ? `− ${fmt$(t.amount)}` : `+ ${fmt$(t.net)}`}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PartnerShell>
  );
}

const S = {
  h1: { fontSize: '28px', fontWeight: '800', letterSpacing: '-0.8px', margin: '0 0 4px' },
  sub: { fontSize: '14px', color: COLOR.textMuted, margin: '0 0 24px' },
  h2: { fontSize: '16px', fontWeight: '700', letterSpacing: '-0.3px', margin: '32px 0 12px' },
  notice: {
    ...glass({ borderRadius: '14px', padding: '14px 16px' }),
    borderColor: 'rgba(74,222,128,0.3)', color: '#4ADE80', fontSize: '13.5px', marginBottom: '16px', lineHeight: 1.5,
  },
  error: {
    ...glass({ borderRadius: '14px', padding: '14px 16px' }),
    borderColor: 'rgba(248,113,113,0.3)', color: '#f87171', fontSize: '13.5px', marginBottom: '16px', lineHeight: 1.5,
  },
  hero: { ...glassRed({ borderRadius: '22px', padding: '28px 24px' }), textAlign: 'center' },
  heroLabel: { fontSize: '10px', letterSpacing: '2.5px', color: COLOR.textFaint, fontWeight: '700' },
  heroValue: { fontSize: '46px', fontWeight: '800', letterSpacing: '-2px', margin: '10px 0 22px', lineHeight: 1 },
  heroSplit: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px',
    paddingBottom: '22px', marginBottom: '20px', borderBottom: `1px solid ${COLOR.glassBorder}`,
  },
  splitValue: { fontSize: '17px', fontWeight: '700' },
  splitLabel: { fontSize: '11px', color: COLOR.textFaint, marginTop: '3px' },
  splitDivider: { width: '1px', height: '32px', background: COLOR.glassBorder },
  cta: {
    width: '100%', background: `linear-gradient(135deg, ${COLOR.accent} 0%, ${COLOR.accentDeep} 100%)`,
    color: '#fff', border: 'none', borderRadius: '14px', padding: '16px',
    fontSize: '16px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
  },
  ctaNote: { fontSize: '11.5px', color: COLOR.textFaint, marginTop: '12px', lineHeight: 1.5 },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  row: { ...glass({ borderRadius: '14px', padding: '14px 16px' }), display: 'flex', alignItems: 'center', gap: '14px' },
  rowTitle: { fontSize: '14px', fontWeight: '600' },
  rowMeta: { fontSize: '11.5px', color: COLOR.textFaint, marginTop: '3px' },
  rowAmount: { fontSize: '15px', fontWeight: '700', flexShrink: 0 },
  empty: {
    ...glass({ borderRadius: '18px', padding: '40px 20px' }),
    textAlign: 'center', fontSize: '14px', color: COLOR.textFaint,
  },
};
